import { useMemo, useState } from 'react';

import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { createTeamApi } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr } from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface PlayerEntry {
  firstName: string;
  lastName: string;
  licenseNumber: string;
}

function emptyPlayer(): PlayerEntry {
  return { firstName: '', lastName: '', licenseNumber: '' };
}

interface CreateTeamModalProps {
  clubId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateTeamModal({ clubId, onClose, onSuccess }: CreateTeamModalProps) {
  const [competitionId, setCompetitionId] = useState<number | ''>('');
  const [prevCompetitionId, setPrevCompetitionId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [captainPhone, setCaptainPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [players, setPlayers] = useState<PlayerEntry[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: competitionsData } = useQuery({
    queryKey: queryKeys.competitions.list({ status: CompetitionStatus.REGISTRATION_OPEN, limit: 100 }),
    queryFn: () => getCompetitionsApi({ status: CompetitionStatus.REGISTRATION_OPEN, limit: 100 }),
    select: res => res.data,
  });
  const competitions: Competition[] = useMemo(() => competitionsData ?? [], [competitionsData]);

  const selectedComp = competitions.find(c => c.id === competitionId);
  const numAdditional = selectedComp?.playersPerTeam ? selectedComp.playersPerTeam - 1 : 0;

  if (prevCompetitionId !== competitionId) {
    setPrevCompetitionId(competitionId);
    setPlayers(Array.from({ length: numAdditional }, emptyPlayer));
    setErrors({});
  }

  function updatePlayer(index: number, field: keyof PlayerEntry, value: string) {
    setPlayers(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  }

  const mutation = useMutation({
    mutationFn: () =>
      createTeamApi({
        name: name.trim(),
        competitionId: competitionId as number,
        clubId,
        captainPhone: captainPhone.trim() || undefined,
        players: players.map(p => ({
          firstName: p.firstName.trim(),
          lastName: p.lastName.trim(),
          licenseNumber: p.licenseNumber.trim(),
        })),
        notes: notes.trim() || undefined,
        acceptedCgu: true,
        // P3b-PR6 — Inscription via back-office club : le CLUB_ADMIN agit par mandat
        // du capitaine (acte de gestion club). La reconnaissance L.221-28 12° est
        // confirmée par l'admin pour le compte du capitaine. Traçabilité côté DB
        // via Team.acknowledgedNoWithdrawalAt (timestamp serveur).
        acknowledgedNoWithdrawal: true,
      }),
    onSuccess: () => {
      toast.success('Equipe creee avec succes');
      onSuccess();
    },
    onError: () => {
      toast.error("Erreur lors de la creation de l'equipe");
    },
  });

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!competitionId) errs.competition = 'Selectionnez un concours';
    if (!name.trim()) errs.name = "Le nom de l'equipe est obligatoire";
    players.forEach((p, i) => {
      if (!p.firstName.trim()) errs[`p${i}_firstName`] = 'Prenom obligatoire';
      if (!p.lastName.trim()) errs[`p${i}_lastName`] = 'Nom obligatoire';
      if (!p.licenseNumber.trim()) errs[`p${i}_license`] = 'Licence obligatoire';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      <div className='relative z-10 w-full sm:max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col'>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl shrink-0'>
          <div>
            <h2 className='text-lg font-bold text-slate-900'>Nouvelle equipe</h2>
            <p className='text-sm text-slate-500 mt-0.5'>Inscrire une equipe a un concours</p>
          </div>
          <button onClick={onClose} aria-label='Fermer' className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
            <X className='w-5 h-5 text-slate-500' />
          </button>
        </div>

        {/* Form */}
        <div className='p-6 space-y-5 flex-1'>
          {/* Competition selector */}
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Concours <span className='text-red-500'>*</span>
            </label>
            <select
              value={competitionId}
              onChange={e => setCompetitionId(e.target.value ? Number(e.target.value) : '')}
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none text-sm bg-white ${
                errors.competition ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
              }`}
            >
              <option value=''>— Selectionner un concours ouvert —</option>
              {competitions.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.date ? formatDateAbbreviatedFr(c.date) : '—'} ({c.playersPerTeam ?? '?'}J,{' '}
                  {c.maxTeams ? c.maxTeams - c.currentTeams : '??'} place
                  {c.maxTeams ? (c.maxTeams - c.currentTeams !== 1 ? 's' : '') : '??'})
                </option>
              ))}
            </select>
            {errors.competition && <p className='text-xs text-red-500 mt-1'>{errors.competition}</p>}
            {competitions.length === 0 && (
              <p className='text-xs text-slate-400 mt-1'>Aucun concours ouvert aux inscriptions pour le moment.</p>
            )}
          </div>

          {/* Team name */}
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Nom de l&apos;equipe <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='Ex : Les Champions du Club'
              maxLength={255}
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none text-sm ${
                errors.name ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'
              }`}
            />
            {errors.name && <p className='text-xs text-red-500 mt-1'>{errors.name}</p>}
          </div>

          {/* Captain phone */}
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Telephone du capitaine <span className='font-normal text-slate-400'>(optionnel)</span>
            </label>
            <input
              type='tel'
              value={captainPhone}
              onChange={e => setCaptainPhone(e.target.value)}
              placeholder='06 12 34 56 78'
              maxLength={15}
              className='w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 text-sm'
            />
          </div>

          {/* Additional players */}
          {selectedComp && numAdditional > 0 && (
            <div>
              <p className='text-sm font-semibold text-slate-700 mb-3'>
                Joueurs additionnels{' '}
                <span className='font-normal text-slate-400'>
                  ({numAdditional} joueur{numAdditional > 1 ? 's' : ''} — vous etes capitaine)
                </span>
              </p>
              <div className='space-y-4'>
                {players.map((player, i) => (
                  <div key={i} className='bg-slate-50 rounded-xl p-4 space-y-3'>
                    <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Joueur {i + 2}</p>
                    <div className='grid grid-cols-2 gap-3'>
                      <div>
                        <input
                          type='text'
                          value={player.firstName}
                          onChange={e => updatePlayer(i, 'firstName', e.target.value)}
                          placeholder='Prenom'
                          maxLength={50}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none text-sm ${
                            errors[`p${i}_firstName`] ? 'border-red-400' : 'border-slate-200 focus:border-emerald-500'
                          }`}
                        />
                        {errors[`p${i}_firstName`] && (
                          <p className='text-xs text-red-500 mt-0.5'>{errors[`p${i}_firstName`]}</p>
                        )}
                      </div>
                      <div>
                        <input
                          type='text'
                          value={player.lastName}
                          onChange={e => updatePlayer(i, 'lastName', e.target.value)}
                          placeholder='Nom'
                          maxLength={50}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none text-sm ${
                            errors[`p${i}_lastName`] ? 'border-red-400' : 'border-slate-200 focus:border-emerald-500'
                          }`}
                        />
                        {errors[`p${i}_lastName`] && (
                          <p className='text-xs text-red-500 mt-0.5'>{errors[`p${i}_lastName`]}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <input
                        type='text'
                        value={player.licenseNumber}
                        onChange={e => updatePlayer(i, 'licenseNumber', e.target.value)}
                        placeholder='Numero de licence FFPJP'
                        maxLength={20}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none text-sm font-mono ${
                          errors[`p${i}_license`] ? 'border-red-400' : 'border-slate-200 focus:border-emerald-500'
                        }`}
                      />
                      {errors[`p${i}_license`] && (
                        <p className='text-xs text-red-500 mt-0.5'>{errors[`p${i}_license`]}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedComp && numAdditional === 0 && (
            <div className='bg-blue-50 rounded-xl p-4 text-sm text-blue-700'>
              Ce concours est en individuel — vous serez le seul joueur de l&apos;equipe.
            </div>
          )}

          {/* Notes */}
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Notes <span className='font-normal text-slate-400'>(optionnel)</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Informations complementaires...'
              maxLength={500}
              rows={2}
              className='w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 text-sm resize-none'
            />
          </div>
        </div>

        {/* Footer */}
        <div className='sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3 shrink-0'>
          <button
            onClick={onClose}
            disabled={mutation.isPending}
            className='flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50'
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending || !competitionId}
            className='flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors'
          >
            {mutation.isPending ? 'Inscription...' : "Inscrire l'equipe"}
          </button>
        </div>
      </div>
    </div>
  );
}
